import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import { nWithCommas } from '../utils';
import { useAnalytics } from '../AnalyticsContext';
import { logEvent } from "firebase/analytics";

const MovimientosCmpt = ({ data = [], saldos = [], titulo, toReturn, isWebView = false }) => {
    const navigate = useNavigate();
    const analytics = useAnalytics();

    const isRetirosData = useMemo(() => {
        return data && data.length > 0 && (
            data[0].IdRetiro !== undefined ||
            data[0].FechaSolicitud !== undefined ||
            data[0].TipoRetiro !== undefined ||
            data[0].Estatus !== undefined
        );
    }, [data]);

    const combinedData = useMemo(() => {
        const arr = [];
        if (saldos && saldos.length > 0) {
            saldos.forEach(s => {
                arr.push({
                    isSaldo: true,
                    Cuenta: s.Cuenta,
                    Abonos: s.Abonos,
                    Retiros: s.Retiros,
                    Rendimientos: s.Rendimientos,
                    Saldo: s.Saldo,
                    Descripcion: s.Cuenta,
                    MontoUnified: s.Saldo,
                });
            });
        }
        if (data && data.length > 0) {
            data.forEach(r => {
                arr.push({
                    isRetiro: true,
                    ...r,
                    Descripcion: r.TipoRetiro || r.Concepto || r.Cuenta,
                    MontoUnified: r.Monto,
                });
            });
        }
        return arr;
    }, [data, saldos]);

    const getThemeColor = () => {
        if (!titulo) return '#202A52';
        const t = titulo.toLowerCase();
        if (t.includes('caja')) return '#A25EB5';
        if (t.includes('fondo de ahorro')) return '#FF5100';
        return '#202A52';
    };

    const themeColor = getThemeColor();

    const tableColumns = useMemo(() => {
        if (isRetirosData || (saldos && saldos.length > 0)) {
            return [
                { key: 'IdRetiro', label: 'Folio' },
                { key: 'FechaSolicitud', label: 'Fecha' },
                { key: 'Descripcion', label: 'Descripción / Cuenta' },
                { key: 'Estatus', label: 'Estatus' },
                { key: 'Abonos', label: 'Abonos' },
                { key: 'Retiros', label: 'Retiros (Total)' },
                { key: 'Rendimientos', label: 'Rendimientos' },
                { key: 'MontoUnified', label: 'Monto / Saldo' }
            ];
        }
        return [
            { key: 'Fecha', label: 'Fecha' },
            { key: 'Concepto', label: 'Concepto' },
            { key: 'Cuenta', label: 'Descripción / Cuenta' },
            { key: 'Monto', label: 'Monto' }
        ];
    }, [isRetirosData, saldos]);

    const formatCell = (colKey, row) => {
        switch (colKey) {
            case 'IdRetiro':
                return row.IdRetiro || '-';
            case 'FechaSolicitud':
            case 'Fecha': {
                const raw = row[colKey] || row.FechaSolicitud || row.Fecha;
                if (!raw) return '-';
                const str = String(raw);
                return str.length === 8 ? `${str.substring(6, 8)}/${str.substring(4, 6)}/${str.substring(0, 4)}` : str;
            }
            case 'Descripcion':
            case 'Cuenta':
            case 'Concepto':
                return row[colKey] || '-';
            case 'Estatus': {
                const status = row.Estatus;
                if (!status) return '-';
                let colorClass = 'vlmx-navy-80';
                const sUpper = status.toUpperCase();
                if (sUpper === 'APLICADO') colorClass = 'green-text';
                else if (sUpper === 'CANCELADO') colorClass = 'red-text';
                else if (sUpper === 'RECIBIDO' || sUpper.includes('PROCESO')) colorClass = 'orange-text';
                return <span className={`txtBold ${colorClass}`}>{status}</span>;
            }
            case 'Abonos': {
                const raw = row.Abonos;
                if (!raw) return '-';
                const numVal = typeof raw === 'string' ? parseFloat(raw.replace(/,/g, '')) : raw;
                const formatted = isNaN(numVal) ? raw : nWithCommas(Math.abs(numVal).toFixed(2));
                return <span className="txtBold">${formatted}</span>;
            }
            case 'Retiros': {
                const raw = row.Retiros;
                if (!row.isSaldo) return '-';
                if (!raw) return '$0.00';
                const numVal = typeof raw === 'string' ? parseFloat(raw.replace(/,/g, '')) : raw;
                const formatted = isNaN(numVal) ? raw : nWithCommas(Math.abs(numVal).toFixed(2));
                return <span className="txtBold red-text">-${formatted}</span>;
            }
            case 'Rendimientos': {
                const raw = row.Rendimientos;
                if (!raw) return '-';
                const numVal = typeof raw === 'string' ? parseFloat(raw.replace(/,/g, '')) : raw;
                const isNegative = numVal < 0;
                const formatted = isNaN(numVal) ? raw : nWithCommas(Math.abs(numVal).toFixed(2));
                return <span className={`txtBold ${isNegative ? 'red-text' : 'green-text'}`}>{isNegative ? '-' : ''}${formatted}</span>;
            }
            case 'MontoUnified':
            case 'Monto': {
                const raw = row[colKey];
                if (raw === undefined || raw === null) return '$0.00';
                const numVal = typeof raw === 'string' ? parseFloat(raw.replace(/,/g, '')) : raw;
                const isNegative = row.Concepto && row.Concepto.toLowerCase().includes('prestamo') && numVal > 0;
                const displayVal = isNegative ? -numVal : numVal;
                const formatted = isNaN(displayVal) ? raw : nWithCommas(Math.abs(displayVal).toFixed(2));
                return <span className={`txtBold ${displayVal < 0 ? 'red-text' : ''}`}>{displayVal < 0 ? '-' : ''}${formatted}</span>;
            }
            default:
                return '-';
        }
    };

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [page, setPage] = useState(0);
    const pageSize = 10;

    React.useEffect(() => {
        if (combinedData && combinedData.length > 0) {
            let maxDate = '';
            combinedData.forEach(row => {
                const rawDate = String(row.FechaSolicitud || row.Fecha || '');
                if (rawDate && rawDate.length >= 6) {
                    if (rawDate > maxDate) {
                        maxDate = rawDate;
                    }
                }
            });

            if (maxDate && maxDate.length >= 6) {
                const year = maxDate.substring(0, 4);
                const month = maxDate.substring(4, 6);
                const start = `${year}-${month}-01`;
                const lastDay = new Date(year, parseInt(month, 10), 0).getDate();
                const end = `${year}-${month}-${lastDay.toString().padStart(2, '0')}`;
                setStartDate(start);
                setEndDate(end);
            }
        }
    }, [combinedData]);

    const filteredData = useMemo(() => {
        if (!startDate && !endDate) return combinedData;
        return combinedData.filter(row => {
            const rawDate = String(row.FechaSolicitud || row.Fecha || '');
            if (!rawDate) return false;
            
            let rowDateValue = rawDate;
            if (rowDateValue.length > 8) {
                rowDateValue = rowDateValue.substring(0, 8);
            } else if (rowDateValue.length === 6) {
                rowDateValue = rowDateValue + '01'; // Default to start of month if only YYYYMM is provided
            }
            
            if (startDate) {
                const startValue = startDate.replace(/-/g, '');
                if (rowDateValue < startValue) return false;
            }
            if (endDate) {
                const endValue = endDate.replace(/-/g, '');
                if (rowDateValue > endValue) return false;
            }
            
            return true;
        });
    }, [combinedData, startDate, endDate]);

    const handleExportData = React.useCallback(() => {
        logEvent(analytics, "descarga_movimientos_csv", {
            screen_name: isWebView ? "webview_movimientos" : "historico_movimientos",
            producto: titulo || '',
            plataforma: isWebView ? "app" : "web"
        });

        const csvConfig = mkConfig({
            fieldSeparator: ',',
            decimalSeparator: '.',
            useKeysAsHeaders: true,
            filename: `Movimientos_${(titulo === 'Fondo de Pensiones' ? 'Plan de Pensiones' : (titulo || '')).replace(/ /g, '_')}`,
        });

        const exportData = filteredData.map(row => {
            if (isRetirosData || row.isSaldo) {
                const raw = String(row.FechaSolicitud || row.Fecha || '');
                const fecha = raw.length === 8 ? `${raw.substring(6, 8)}/${raw.substring(4, 6)}/${raw.substring(0, 4)}` : raw;
                return {
                    Folio: row.IdRetiro ?? '',
                    Fecha: fecha,
                    'Descripción / Cuenta': row.Descripcion ?? '',
                    Estatus: row.Estatus ?? '',
                    Abonos: row.Abonos ?? '',
                    Retiros: row.Retiros ?? '',
                    Rendimientos: row.Rendimientos ?? '',
                    'Monto / Saldo': row.MontoUnified ?? '',
                };
            }
            const raw = String(row.Fecha || '');
            const fecha = raw.length === 8 ? `${raw.substring(6, 8)}/${raw.substring(4, 6)}/${raw.substring(0, 4)}` : raw;
            return {
                Fecha: fecha,
                Concepto: row.Concepto ?? '',
                Cuenta: row.Cuenta ?? '',
                Monto: row.Concepto && row.Concepto.toLowerCase().includes('prestamo') ? row.Monto * -1 : row.Monto,
            };
        });

        const csv = generateCsv(csvConfig)(exportData);
        download(csvConfig)(csv);
    }, [analytics, filteredData, isRetirosData, isWebView, titulo]);

    React.useEffect(() => {
        window.downloadMovimientosCSV = () => {
            handleExportData();
        };
        return () => {
            delete window.downloadMovimientosCSV;
        };
    }, [handleExportData]);

    const handleStartDateChange = (e) => {
        setStartDate(e.target.value);
        setPage(0);
    };

    const handleEndDateChange = (e) => {
        setEndDate(e.target.value);
        setPage(0);
    };

    const totalPages = Math.ceil(filteredData.length / pageSize) || 1;
    const paginatedData = filteredData.slice(page * pageSize, (page + 1) * pageSize);

    return (
        <div className="container" style={{ marginTop: '2rem' }}>
            <div className="row">
                <div className="col s12 left-align">
                    {toReturn && !isWebView && (
                        <p
                            className="cursorPointer"
                            onClick={() => {
                                navigate(toReturn);
                            }}
                        >
                            <i className="material-icons left">keyboard_backspace</i> {titulo}
                        </p>
                    )}
                    <h5 className="txtBold" style={{ color: themeColor }}>{titulo}</h5>
                    <p className="vlmx-navy-80">Historial de movimientos</p>
                </div>
            </div>

            <div className="row" style={{ marginTop: '1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                <div className="col s12 m4 l3" style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: '12px', color: themeColor, fontWeight: 'bold' }}>Fecha inicio</span>
                        <input
                            type="date"
                            value={startDate}
                            onChange={handleStartDateChange}
                            style={{ border: `1px solid ${themeColor}`, borderRadius: '4px', height: '36px', outline: 'none', padding: '0 10px', boxSizing: 'border-box' }}
                        />
                    </div>
                </div>
                <div className="col s12 m4 l3" style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: '12px', color: themeColor, fontWeight: 'bold' }}>Fecha fin</span>
                        <input
                            type="date"
                            value={endDate}
                            onChange={handleEndDateChange}
                            style={{ border: `1px solid ${themeColor}`, borderRadius: '4px', height: '36px', outline: 'none', padding: '0 10px', boxSizing: 'border-box' }}
                        />
                    </div>
                </div>
                <div className="col s12 m4 l6 right-align" style={{ marginBottom: '10px' }}>
                    <button 
                        className="btn" 
                        onClick={handleExportData} 
                        style={{ 
                            backgroundColor: '#3b4566', 
                             borderRadius: '8px',
                            color: 'white', 
                            display: 'inline-flex', 
                            alignItems: 'center',
                            textTransform: 'none',
                            fontWeight: 'bold',
                            boxShadow: 'none'
                        }}
                    >
                        <i className="material-icons left" style={{ marginRight: '8px' }}>file_download</i> 
                        Descargar CSV
                    </button>
                </div>
            </div>

            <div className="row mt-2">
                <div className="col s12">
                    <div style={{ overflowX: 'auto', border: `1px solid ${themeColor}`, borderRadius: '8px' }}>
                        <table className="striped centered" style={{ margin: 0, width: '100%', borderCollapse: 'collapse', border: 'none' }}>
                            <thead style={{ backgroundColor: themeColor, color: 'white' }}>
                                <tr>
                                    {tableColumns.map((col, index) => (
                                        <th 
                                            key={col.key} 
                                            style={{ 
                                                padding: '12px 10px', 
                                                textAlign: 'center', 
                                                fontWeight: 'bold',
                                                borderRight: index < tableColumns.length - 1 ? '1px solid white' : 'none'
                                            }}
                                        >
                                            <div>{col.label}</div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedData.length > 0 ? paginatedData.map((row, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #e0e0e0' }}>
                                        {tableColumns.map(col => (
                                            <td key={col.key} style={{ padding: '12px 10px', verticalAlign: 'middle' }}>
                                                {formatCell(col.key, row)}
                                            </td>
                                        ))}
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={tableColumns.length} style={{ padding: '20px', textAlign: 'center' }}>
                                            No hay movimientos para mostrar
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: '1rem', gap: '1rem' }}>
                        <button 
                            disabled={page === 0} 
                            onClick={() => setPage(p => p - 1)}
                            className="btn"
                            style={{ 
                                backgroundColor: page === 0 ? '#e0e0e0' : themeColor, 
                                color: page === 0 ? '#9e9e9e' : 'white',
                                boxShadow: 'none'
                            }}
                        >
                            Anterior
                        </button>
                        <span className="txtBold">Página {page + 1} de {totalPages}</span>
                        <button 
                            disabled={page >= totalPages - 1} 
                            onClick={() => setPage(p => p + 1)}
                            className="btn"
                            style={{ 
                                backgroundColor: page >= totalPages - 1 ? '#e0e0e0' : themeColor, 
                                color: page >= totalPages - 1 ? '#9e9e9e' : 'white',
                                boxShadow: 'none'
                            }}
                        >
                            Siguiente
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default MovimientosCmpt;